import {defineMessage} from '../../../libs/shared/helpers/src/internationalization/message';
import {isEnumType} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {NewPaletteColorsEnum} from '../../../libs/shared/ui/src/styles/colorStyles';
import {LocalizeRenderer} from '../../../srcCommon/internationalization/internationalizationContext';
import {
  AnalyticsTableMetric,
  isNumericDataPoint,
  isSubmetricDataPoint,
} from '../../models/api/analyticsMetricsModel';
import {Localizer} from '../../store/stateless/internationalizationSelectors';
import {RawSankeyData} from '../components/charts/components/sankey/sankey';
import {TableToGraphTransformer} from './analyticsMetricsTransformers';
import {
  AnalyticsComputedGraphMetricBase,
  AnalyticsComputedGraphMetricsEnum,
  AnalyticsComputedMetricTypesEnum,
} from './analyticsMetricsTransformersTypes';

/*
 * Types.
 */

export enum SankeySubmetricNodesEnum {
  CLOSED_AT_END = 'closedAtEnd',
  OPEN_AT_END = 'openAtEnd',
  NEW_THIS_PERIOD = 'periodNew',
  OPEN_FROM_PREVIOUS_PERIODS = 'backlogOpen',
  REPLIED = 'replied',
  UNREPLIED = 'unreplied',
}

export enum SankeySubmetricLinksEnum {
  CLOSED_FROM_REPLIED = 'closedFromReplied',
  CLOSED_FROM_UNREPLIED = 'closedFromUnreplied',
  OPEN_FROM_REPLIED = 'openFromReplied',
  OPEN_FROM_UNREPLIED = 'openFromUnreplied',
  REPLIED_FROM_BACKLOG = 'repliedFromBacklog',
  REPLIED_FROM_PERIOD = 'repliedFromPeriod',
  UNREPLIED_FROM_BACKLOG = 'unrepliedFromBacklog',
  UNREPLIED_FROM_PERIOD = 'unrepliedFromPeriod',
}

interface SankeyLinkSubmetrics {
  [SankeySubmetricLinksEnum.CLOSED_FROM_REPLIED]: number;
  [SankeySubmetricLinksEnum.CLOSED_FROM_UNREPLIED]: number;
  [SankeySubmetricLinksEnum.OPEN_FROM_REPLIED]: number;
  [SankeySubmetricLinksEnum.OPEN_FROM_UNREPLIED]: number;
  [SankeySubmetricLinksEnum.REPLIED_FROM_BACKLOG]: number;
  [SankeySubmetricLinksEnum.REPLIED_FROM_PERIOD]: number;
  [SankeySubmetricLinksEnum.UNREPLIED_FROM_BACKLOG]: number;
  [SankeySubmetricLinksEnum.UNREPLIED_FROM_PERIOD]: number;
}

export type SankeySubmetricsEnum = SankeySubmetricNodesEnum | SankeySubmetricLinksEnum;

export type SankeyGraphMetricValue = RawSankeyData;

export interface SankeyGraphMetric extends AnalyticsComputedGraphMetricBase<SankeyGraphMetricValue> {}

/*
 * Const.
 */

// Labels for each submetric.
const sankeySubmetricsLabels: Record<SankeySubmetricsEnum, LocalizeRenderer> = {
  [SankeySubmetricNodesEnum.CLOSED_AT_END]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-closed-workload',
        message: 'Closed workload',
      }),
    ),
  [SankeySubmetricNodesEnum.NEW_THIS_PERIOD]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-new-workload',
        message: 'New workload',
      }),
    ),
  [SankeySubmetricNodesEnum.OPEN_FROM_PREVIOUS_PERIODS]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-open-at-start',
        message: 'Open at start',
      }),
    ),
  [SankeySubmetricNodesEnum.REPLIED]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-replied-to',
        message: 'Replied to',
      }),
    ),
  [SankeySubmetricNodesEnum.OPEN_AT_END]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-open-at-end',
        message: 'Open at end',
      }),
    ),
  [SankeySubmetricNodesEnum.UNREPLIED]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-unreplied-to',
        message: 'Unreplied to',
      }),
    ),
  [SankeySubmetricLinksEnum.CLOSED_FROM_REPLIED]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-replied-to-closed',
        message: 'Replied to → Closed',
      }),
    ),
  [SankeySubmetricLinksEnum.CLOSED_FROM_UNREPLIED]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-unreplied-to-closed',
        message: 'Unreplied to → Closed',
      }),
    ),
  [SankeySubmetricLinksEnum.OPEN_FROM_REPLIED]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-replied-to-still-open',
        message: 'Replied to → Still open',
      }),
    ),
  [SankeySubmetricLinksEnum.OPEN_FROM_UNREPLIED]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-unreplied-to-still-open',
        message: 'Unreplied to → Still open',
      }),
    ),
  [SankeySubmetricLinksEnum.REPLIED_FROM_BACKLOG]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-open-from-previous-replied-to',
        message: 'Open from previous periods → Replied to',
      }),
    ),
  [SankeySubmetricLinksEnum.REPLIED_FROM_PERIOD]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-new-replied-to',
        message: 'New this period → Replied to',
      }),
    ),
  [SankeySubmetricLinksEnum.UNREPLIED_FROM_BACKLOG]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-open-from-previous-unreplied-to',
        message: 'Open from previous periods → Unreplied to',
      }),
    ),
  [SankeySubmetricLinksEnum.UNREPLIED_FROM_PERIOD]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-new-unreplied-to',
        message: 'New this period → Unreplied to',
      }),
    ),
};

/*
 * Transformer.
 */

export const sankeyTableToGraph: TableToGraphTransformer = (
  metricData: AnalyticsTableMetric | undefined,
  localize: Localizer,
): SankeyGraphMetric => {
  const transformedMetricBase = {
    id: AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH,
    metricType: AnalyticsComputedMetricTypesEnum.CUSTOM_GRAPH,
    isEmpty: metricData?.isEmpty ?? false,
  };

  if (!metricData) {
    return {
      ...transformedMetricBase,
      values: [],
    };
  }

  // Format the submetric in each table row to a flat object.
  const linkSubmetrics = metricData.rows.reduce<Partial<SankeyLinkSubmetrics>>((acc, row) => {
    const submetricNameRow = row[0];
    const submetricValueRow = row[1];

    if (isSubmetricDataPoint(submetricNameRow) && isNumericDataPoint(submetricValueRow)) {
      const submetricName = row[0].value;
      const submetricValue = row[1].value;
      if (!isEnumType(submetricName, SankeySubmetricLinksEnum)) {
        return acc;
      }

      return {
        ...acc,
        [submetricName]: submetricValue,
      };
    }

    return acc;
  }, {});

  return {
    ...transformedMetricBase,
    values: [
      {
        id: SankeySubmetricNodesEnum.REPLIED,
        name: getSankeySubmetricLabel(localize, SankeySubmetricNodesEnum.REPLIED),
        color: NewPaletteColorsEnum.GREEN,
        links: [
          {
            id: SankeySubmetricLinksEnum.CLOSED_FROM_REPLIED,
            targetId: SankeySubmetricNodesEnum.CLOSED_AT_END,
            value: linkSubmetrics[SankeySubmetricLinksEnum.CLOSED_FROM_REPLIED] || 0,
          },
          {
            id: SankeySubmetricLinksEnum.OPEN_FROM_REPLIED,
            targetId: SankeySubmetricNodesEnum.OPEN_AT_END,
            value: linkSubmetrics[SankeySubmetricLinksEnum.OPEN_FROM_REPLIED] || 0,
          },
        ],
      },
      {
        id: SankeySubmetricNodesEnum.NEW_THIS_PERIOD,
        name: getSankeySubmetricLabel(localize, SankeySubmetricNodesEnum.NEW_THIS_PERIOD),
        color: NewPaletteColorsEnum.RED,
        links: [
          {
            id: SankeySubmetricLinksEnum.REPLIED_FROM_PERIOD,
            targetId: SankeySubmetricNodesEnum.REPLIED,
            value: linkSubmetrics[SankeySubmetricLinksEnum.REPLIED_FROM_PERIOD] || 0,
          },
          {
            id: SankeySubmetricLinksEnum.UNREPLIED_FROM_PERIOD,
            targetId: SankeySubmetricNodesEnum.UNREPLIED,
            value: linkSubmetrics[SankeySubmetricLinksEnum.UNREPLIED_FROM_PERIOD] || 0,
          },
        ],
      },
      {
        id: SankeySubmetricNodesEnum.CLOSED_AT_END,
        name: getSankeySubmetricLabel(localize, SankeySubmetricNodesEnum.CLOSED_AT_END),
        color: NewPaletteColorsEnum.YELLOW,
        links: [],
      },
      {
        id: SankeySubmetricNodesEnum.OPEN_AT_END,
        name: getSankeySubmetricLabel(localize, SankeySubmetricNodesEnum.OPEN_AT_END),
        color: NewPaletteColorsEnum.ORANGE,
        links: [],
      },
      {
        id: SankeySubmetricNodesEnum.UNREPLIED,
        name: getSankeySubmetricLabel(localize, SankeySubmetricNodesEnum.UNREPLIED),
        color: NewPaletteColorsEnum.BLUE,
        links: [
          {
            id: SankeySubmetricLinksEnum.CLOSED_FROM_UNREPLIED,
            targetId: SankeySubmetricNodesEnum.CLOSED_AT_END,
            value: linkSubmetrics[SankeySubmetricLinksEnum.CLOSED_FROM_UNREPLIED] || 0,
          },
          {
            id: SankeySubmetricLinksEnum.OPEN_FROM_UNREPLIED,
            targetId: SankeySubmetricNodesEnum.OPEN_AT_END,
            value: linkSubmetrics[SankeySubmetricLinksEnum.OPEN_FROM_UNREPLIED] || 0,
          },
        ],
      },
      {
        id: SankeySubmetricNodesEnum.OPEN_FROM_PREVIOUS_PERIODS,
        name: getSankeySubmetricLabel(localize, SankeySubmetricNodesEnum.OPEN_FROM_PREVIOUS_PERIODS),
        color: NewPaletteColorsEnum.GREY,
        links: [
          {
            id: SankeySubmetricLinksEnum.REPLIED_FROM_BACKLOG,
            targetId: SankeySubmetricNodesEnum.REPLIED,
            value: linkSubmetrics[SankeySubmetricLinksEnum.REPLIED_FROM_BACKLOG] || 0,
          },
          {
            id: SankeySubmetricLinksEnum.UNREPLIED_FROM_BACKLOG,
            targetId: SankeySubmetricNodesEnum.UNREPLIED,
            value: linkSubmetrics[SankeySubmetricLinksEnum.UNREPLIED_FROM_BACKLOG] || 0,
          },
        ],
      },
    ],
  };
};

/*
 * Helpers.
 */

export function getSankeySubmetricLabel(localize: Localizer, submetric: SankeySubmetricsEnum): string {
  return sankeySubmetricsLabels[submetric]?.(localize) || submetric;
}
